import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

/**
 * Rule PM9: a disabled-with-explanation control stays keyboard- and
 * screen-reader-reachable. A native `disabled` attribute drops the control
 * out of the tab order and, in some accessibility trees, out of the tree
 * entirely — exactly where an explanation (a visible reason, `V5`; an
 * `aria-describedby`, `PM3`/`PM4`) would otherwise be discoverable. Every
 * button-shaped control in this application renders logical disablement this
 * way instead — one implementation (rule F2), so a control never has to
 * pick between the accessible mechanism and the ordinary one depending on
 * whether it happens to carry an explanation today.
 *
 * `aria-disabled` alone does not stop a real `<button>` from being clicked or
 * activated by keyboard, so the click itself has to be refused here — for a
 * `type="submit"` control this also has to stop the browser's own implicit
 * form submission, which is why `preventDefault` runs unconditionally rather
 * than only skipping the caller's own handler.
 */
export function disabledButtonProps(
  disabled: boolean | undefined,
  onClick: ((event: React.MouseEvent<HTMLButtonElement>) => void) | undefined,
): {
  'aria-disabled': true | undefined;
  onClick: ((event: React.MouseEvent<HTMLButtonElement>) => void) | undefined;
} {
  return {
    'aria-disabled': disabled ? true : undefined,
    onClick: disabled
      ? (event: React.MouseEvent<HTMLButtonElement>) => {
          event.preventDefault();
        }
      : onClick,
  };
}

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all aria-disabled:pointer-events-none aria-disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground shadow-xs hover:bg-primary/90',
        destructive: 'bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        outline: 'border bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        secondary: 'bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  disabled,
  onClick,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...disabledButtonProps(disabled, onClick)}
      {...props}
    />
  );
}

export { Button, buttonVariants };
